import { MenuItem, Tooltip, Typography, Drawer, Grid, Divider } from '@material-ui/core';
import { NotInterested, Description } from '@material-ui/icons';
import { useGenericStyles } from 'common/genericStyles';
import { Cancel, CheckCircle } from '@material-ui/icons';
import {
  DataGridPro,
  getGridDateOperators,
  getGridNumericOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridCellParams,
  GridFilterModel,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import { BloqueioResponse, BloqueioTipo, BloqueiosFilter, GrupoAcesso, Perfil } from 'common/genericTypes';
import MoreOptionsMenu from 'components/moreOptionsMenu';
import { Button, MdSpace, SmSpace, TextField } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { RootState } from 'store';
import UploadDocs, { Attachment } from 'components/uploadDocs';
import { bloqueioTipoDescriptions } from 'common/genericDescriptions';
import { GenericToast, GenericToastKind } from 'components/genericToast';
import { addAlert } from 'store/modules/snackBar';
import DownloadIcon from '@material-ui/icons/GetApp';
import CustomToolBarUtils from 'utils/dataGrid/customToolBar';
import { componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { formatDate } from 'utils/date';
import { formatNumber } from 'utils/number';

const ListaConstricoesGarantias: React.FC = () => {
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);

  const [bloqueios, setBloqueios] = useState<Array<BloqueioResponse>>([]);
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [bloqueiosFilter, setBloqueiosFilter] = useState<Array<BloqueiosFilter>>([]);

  const [selectedBloqueio, setSelectedBloqueio] = useState<BloqueioResponse>();
  const [bloqueioTipoLiberado, setBloqueioTipoLiberado] = useState<BloqueioTipo>();

  const [justificativa, setJustificativa] = useState<string>('');
  const [anexos, setAnexos] = useState<Array<Attachment>>([]);

  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useGenericStyles();
  const { postBloqueiosPaginated, postLiberarBloqueio } = useSelector((state: RootState) => state.investidorApi);
  const isUsuarioOperacoes = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes
  );
  const isUsuarioCadastroOperacoes = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes
  );
  const isUsuarioLeitura = useSelector((state: RootState) => state.auth.usuario.participante.perfil === Perfil.Leitura);
  const { postCertidaoGravame } = useSelector((state: RootState) => state.investidorApi);

  const getBloqueios = useCallback(async () => {
    try {
      const result = await postBloqueiosPaginated(
        dispatch,
        {
          page: page + 1,
          pageSize,
          linkOperator,
          bloqueiosFilter,
        },
        true
      );
      setBloqueios(result.result);
      setPageCount(result.totalCount);
    } catch {}
  }, [dispatch, postBloqueiosPaginated, page, pageSize, linkOperator, bloqueiosFilter]);

  useEffect(() => {
    getBloqueios();
  }, [getBloqueios]);

  const handleOpenDrawer = (bloqueio: BloqueioResponse) => {
    setSelectedBloqueio(bloqueio);
    setOpenDrawer(true);
  };

  const getStatusIcon = (liberado: boolean) => {
    if (!liberado) {
      return <Cancel fontSize="small" className={classes.errorIcon} style={{ marginRight: 10 }} />;
    }
    return <CheckCircle fontSize="small" className={classes.successIcon} style={{ marginRight: 10 }} />;
  };

  const handleLiberarBloqueio = async (bloqueioId: string) => {
    if (selectedBloqueio?.tipo === BloqueioTipo.Constricao && !justificativa && anexos.length === 0) {
      dispatch(addAlert({ severity: 'error', message: 'É obrigatório o preenchimento da justificativa ou anexo.' }));
      return;
    }

    try {
      const result = await postLiberarBloqueio(
        dispatch,
        bloqueioId,
        justificativa,
        anexos?.filter((_) => _.file)?.map((_) => _.file!),
        true
      );
      setBloqueioTipoLiberado(result.tipo);
      handleCloseDrawer();
      getBloqueios();
    } catch (error) {}
  };

  const handleCloseDrawer = () => {
    setSelectedBloqueio(undefined);
    setOpenDrawer(false);
  };

  const handleRemoveAnexo = (id: string) => {
    const updatedAttachments = anexos.filter((anexo) => anexo.id !== id);
    setAnexos(updatedAttachments);
  };

  const handleAnexosChange = (newAttachments: Attachment[]) => {
    const updatedAttachments = anexos.concat(newAttachments);
    setAnexos(updatedAttachments);
  };

  async function handleDownload(bloqueioId: string) {
    var bloqueioIdArray = [];
    bloqueioIdArray = [bloqueioId];
    await postCertidaoGravame(dispatch, bloqueioIdArray, true);
  }

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    if (!filterModel.items) {
      setBloqueiosFilter([]);
      setPage(0);
      return;
    }

    setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');

    const newBloqueiosFilter = new Array<BloqueiosFilter>();
    filterModel.items.forEach((filter) => {
      const operatorValue =
        filter.operatorValue === 'is'
          ? 'EQUALS'
          : filter.operatorValue === 'not'
          ? 'NOT_EQUALS'
          : filter.operatorValue == 'startsWith'
          ? 'STARTS_WITH'
          : filter.operatorValue == 'endsWith'
          ? 'ENDS_WITH'
          : filter.operatorValue?.toUpperCase();

      let formattedValue = filter.value?.toString();
      if (formattedValue) {
        if (filter.columnField === 'DATA_DE_CRIACAO') {
          const parts = formattedValue.split('-');
          if (parts.length === 3) {
            formattedValue = `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
        }

        if (filter.columnField === 'STATUS') {
          formattedValue = formattedValue.toUpperCase();
        }

        if (filter.columnField === 'TIPO') {
          const normalized = formattedValue
            .trim()
            .toUpperCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

          if (normalized === 'GARANTIA') {
            formattedValue = BloqueioTipo.Garantia;
          } else if (normalized === 'CONSTRICAO') {
            formattedValue = BloqueioTipo.Constricao;
          }
        }

        const params = {
          columnType: filter.columnField.toUpperCase(),
          operationFilterType: operatorValue,
          value: formattedValue,
        } as BloqueiosFilter;

        newBloqueiosFilter.push(params);
      }
    });

    setBloqueiosFilter(newBloqueiosFilter);
    setPage(0);
  }, []);

  const onSortChange = useCallback(
    (sortModel: GridSortModel) => {
      const newBloqueiosFilter = bloqueiosFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<BloqueiosFilter>;

      if (sortModel.length === 0 || sortModel == null) {
        setBloqueiosFilter([...newBloqueiosFilter]);
        setPage(0);
        return;
      }

      const filter = sortModel[0];
      const params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as BloqueiosFilter;

      newBloqueiosFilter.push(params);
      setBloqueiosFilter([...newBloqueiosFilter]);
      setPage(0);
    },
    [bloqueiosFilter]
  );

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith', 'is'].includes(value)
  );

  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({ value }) => ['is', 'not'].includes(value));

  const filterOperatorsData = getGridDateOperators().filter(({ value }) =>
    ['is', 'not', 'after', 'onOrAfter', 'before', 'onOrBefore'].includes(value)
  );

  const filterOperatorsNumeric = getGridNumericOperators().filter(({ value }) =>
    ['=', '!=', '>', '>=', '<', '<='].includes(value)
  );

  const statusValueOptions = [
    { value: 'BLOQUEADO', label: 'Bloqueado' },
    { value: 'LIBERADO', label: 'Liberado' },
  ];

  const getGridColumns = useCallback(
    () => [
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'singleSelect',
        valueOptions: statusValueOptions,
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: false,
        disableExport: false,
        width: 80,
        minWidth: 60,
        maxWidth: 100,
        valueGetter: (params: GridValueGetterParams) => ((params.row as BloqueioResponse).liberado ? 'LIBERADO' : 'BLOQUEADO'),
        renderCell: (params: GridCellParams) => {
          const bloqueio = params.row as BloqueioResponse;
          return (
            <Tooltip title={bloqueio.liberado ? 'Liberado' : 'Bloqueado'} aria-label={bloqueio.liberado ? 'Liberado' : 'Bloqueado'}>
              {getStatusIcon(bloqueio.liberado)}
            </Tooltip>
          );
        },
      },
      {
        field: 'DATA_DE_CRIACAO',
        headerName: 'Data criação',
        type: 'date',
        filterOperators: filterOperatorsData,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 220,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) => (params.row as BloqueioResponse).dataDeCriacao,
        renderCell: (params: GridCellParams) => {
          const bloqueio = params.row as BloqueioResponse;
          return (
            <Typography variant={'body2'}>{formatDate(bloqueio.dataDeCriacao)}</Typography>
          );
        },
      },
      {
        field: 'TIPO',
        headerName: 'Tipo',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) => (params.row as BloqueioResponse).tipo,
        renderCell: (params: GridCellParams) => {
          const bloqueio = params.row as BloqueioResponse;
          return <Typography variant={'body2'}>{bloqueioTipoDescriptions[bloqueio.tipo]}</Typography>;
        },
      },
      {
        field: 'CODIGO',
        headerName: 'Código',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) => (params.row as BloqueioResponse).instrumentoFinanceiro.codigo,
        renderCell: (params: GridCellParams) => {
          const bloqueio = params.row as BloqueioResponse;
          return <Typography variant={'body2'}>{bloqueio.instrumentoFinanceiro.codigo}</Typography>;
        },
      },
      {
        field: 'QUANTIDADE',
        headerName: 'Quantidade',
        type: 'number',
        filterOperators: filterOperatorsNumeric,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 200,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) => (params.row as BloqueioResponse).quantidade,
        renderCell: (params: GridCellParams) => {
          const bloqueio = params.row as BloqueioResponse;
          return (
            <Typography variant={'body2'}>
              {formatNumber(bloqueio.quantidade, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: ' ',
        filterable: false,
        sortable: false,
        disableExport: true,
        align: 'right' as const,
        headerAlign: 'right' as const,
        width: 150,
        minWidth: 100,
        maxWidth: 200,
        renderCell: (params: GridCellParams) => {
          const bloqueio = params.row as BloqueioResponse;
          return (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
              <MoreOptionsMenu disabled={isUsuarioLeitura}>
                {!isUsuarioLeitura && (isUsuarioOperacoes || isUsuarioCadastroOperacoes) && !bloqueio.liberado && (
                  <MenuItem onClick={() => handleOpenDrawer(bloqueio)}>
                    <CheckCircleIcon fontSize="small" style={{ marginRight: '10px' }} />
                    {`Liberar ${bloqueioTipoDescriptions[bloqueio.tipo].toLocaleLowerCase()}`}
                  </MenuItem>
                )}
                {!isUsuarioLeitura && (
                  <MenuItem onClick={() => history.push(`investidores/bloqueios/detalhes/${bloqueio.id}`)}>
                    <Description fontSize="small" style={{ marginRight: '10px' }} />
                    {`Detalhes`}
                  </MenuItem>
                )}
                {!isUsuarioLeitura && (
                  <MenuItem
                    onClick={() => {
                      handleDownload(bloqueio.id);
                    }}
                  >
                    <DownloadIcon fontSize="small" style={{ marginRight: '10px' }} />
                    {'Download'}
                  </MenuItem>
                )}
              </MoreOptionsMenu>
            </div>
          );
        },
      },
    ],
    [
      filterOperators,
      filterOperatorsData,
      filterOperatorsNumeric,
      filterOperatorsSelect,
      statusValueOptions,
      history,
      isUsuarioCadastroOperacoes,
      isUsuarioLeitura,
      isUsuarioOperacoes,
    ]
  );

  const LiberarConstricaoDrawer = (
    <Drawer
      anchor="right"
      open={openDrawer}
      onClose={handleCloseDrawer}
      PaperProps={{ style: { padding: '40px', width: 520 } }}
    >
      <Grid container>
        <Grid item sm={12}>
          <Typography variant="h3">{`Liberar ${bloqueioTipoDescriptions[selectedBloqueio?.tipo ?? '']}`}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <Typography variant="body2" display={'inline'}>
            {`Tem certeza que deseja liberar a ${
              bloqueioTipoDescriptions[selectedBloqueio?.tipo ?? '']?.toLocaleLowerCase() ?? ''
            } deste ativo?`}
          </Typography>
          <SmSpace />
          {selectedBloqueio?.tipo === BloqueioTipo.Constricao && (
            <>
              <TextField
                label="Justificativa"
                value={justificativa}
                rows={5}
                onChange={setJustificativa}
                multiline
                fullWidth
              />
              <SmSpace />
              <UploadDocs onChange={handleAnexosChange} onDelete={handleRemoveAnexo} multipleFiles />
              <SmSpace />
            </>
          )}
          <Button
            variant="contained"
            fullWidth
            color={'primary'}
            onClick={() => handleLiberarBloqueio(selectedBloqueio?.id ?? '')}
          >
            {`Liberar ${bloqueioTipoDescriptions[selectedBloqueio?.tipo ?? '']}`}
          </Button>
          <SmSpace />
          <Button variant="outlined" fullWidth onClick={handleCloseDrawer}>
            {'Cancelar'}
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );

  return (
    <>
      {bloqueioTipoLiberado && (
        <>
          <GenericToast kind={GenericToastKind.Success} onDismiss={() => setBloqueioTipoLiberado(undefined)}>
            &nbsp;
            <Typography variant="body2" display={'inline'}>
              {bloqueioTipoDescriptions[bloqueioTipoLiberado]}
            </Typography>
            &nbsp;
            <Typography variant="body2" display={'inline'}>
              {'liberada com sucesso.'}
            </Typography>
          </GenericToast>
          <MdSpace />
        </>
      )}
      {isUsuarioOperacoes && !isUsuarioLeitura && (
        <>
          <Button
            size={'small'}
            variant={'outlined'}
            onClick={() => history.push('/gerenciar/investidores/bloqueios/cadastro')}
          >
            <NotInterested fontSize={'small'} className={classes.buttonIcon} />
            {'Novo Bloqueio'}
          </Button>
          <MdSpace />
        </>
      )}
      <MdSpace />
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={componentsPropsValues}
        getRowId={(row) => (row as BloqueioResponse).id}
        rows={bloqueios}
        columns={getGridColumns()}
        components={{
          Toolbar: CustomToolBarUtils,
        }}
        disableSelectionOnClick
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        pageSize={pageSize}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />
      {LiberarConstricaoDrawer}
    </>
  );
};

export default ListaConstricoesGarantias;
